import type { BoxCoordinates } from "../../utils/coordinates";
import type { BoxAnchor, FramePrediction, IntervalResult, TrackingInterval, TrackingPrediction } from "../contracts";
import { boxToRoiRect, roiRectToBox, validBox } from "../geometry";
import type { RecoveryLineage } from "./contracts";
import { frameKey, validRecoveryBox } from "./planning";

export function recoveryChildren(parent: TrackingInterval, anchor: BoxAnchor): [TrackingInterval, TrackingInterval] {
  return [
    {
      id: `${parent.id}::recovery:left:${frameKey(anchor.time)}`,
      trackId: parent.trackId,
      start: parent.start,
      end: anchor,
    },
    {
      id: `${parent.id}::recovery:right:${frameKey(anchor.time)}`,
      trackId: parent.trackId,
      start: anchor,
      end: parent.end,
    },
  ];
}

/** Compose on the original frame grid without replacing a single already trusted local/GT result. */
export function composeRecovery(
  base: IntervalResult,
  anchor: BoxAnchor,
  lineage: RecoveryLineage,
  children: readonly IntervalResult[] = [],
): IntervalResult {
  const predictions = new Map<number, FramePrediction>();
  for (const child of children) {
    if (child.decode.sourceWidth !== base.decode.sourceWidth || child.decode.sourceHeight !== base.decode.sourceHeight)
      continue;
    const convert = (box: NonNullable<FramePrediction["box"]>) => {
      const normalized = roiRectToBox(box, child.decode);
      return validRecoveryBox(normalized) ? boxToRoiRect(normalized, base.decode) : null;
    };
    const diagnostic = (prediction: TrackingPrediction | null): TrackingPrediction | null => {
      if (!prediction) return null;
      const box = convert(prediction.box);
      return box ? { ...prediction, box } : null;
    };
    for (const frame of child.frames) {
      // A private recovered endpoint must never become an original GT anchor.
      if (frame.method === "anchor" || frame.method === "unavailable" || frame.method === "model-recovery") continue;
      const box = convert(frame.box);
      if (!validBox(box)) continue;
      predictions.set(frameKey(frame.timestamp), {
        ...frame,
        box,
        sparse: diagnostic(frame.sparse),
        nano: diagnostic(frame.nano),
        recovery: lineage,
      });
    }
  }
  const directBox = boxToRoiRect(anchor.coordinates as BoxCoordinates, base.decode);
  let changed = false;
  const frames = base.frames.map((frame, index): FramePrediction => {
    if (frame.method !== "unavailable" || index === 0 || index === base.frames.length - 1) return frame;
    if (frameKey(frame.timestamp) === frameKey(anchor.time)) {
      changed = true;
      return {
        ...frame,
        method: "model-recovery",
        box: directBox,
        confidence: null,
        sparse: null,
        nano: null,
        recovery: lineage,
      };
    }
    const replacement = predictions.get(frameKey(frame.timestamp));
    if (!replacement) return frame;
    changed = true;
    return { ...replacement, timestamp: frame.timestamp, duration: frame.duration };
  });
  return changed ? { ...base, frames } : base;
}
